import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import api from '../services/api';
import { formatMonto, formatFecha } from '../utils/format';
import { descargarArchivo } from './reportes/Exportar';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

// ============================================================
// Antigüedad de saldos (#101 CxP / #117 CxC): tarjetas con el total por
// tramo de días vencidos y una tabla con una fila por proveedor/cliente.
// `base` es '/compras' o '/facturas'; ambos exponen GET {base}/antiguedad.
// Los días se cuentan desde el vencimiento (fecha + días de crédito).
// ============================================================

const TRAMOS = [
  { key: 'por_vencer', label: 'Por vencer', clase: 'text-slate-700' },
  { key: 'd30', label: '1-30 días', clase: 'text-slate-700' },
  { key: 'd60', label: '31-60 días', clase: 'text-amber-700' },
  { key: 'd60mas', label: '+60 días', clase: 'text-red-700' }
];

function tramoDe(dias) {
  const d = Number(dias) || 0;
  if (d <= 0) return 'por_vencer';
  if (d <= 30) return 'd30';
  if (d <= 60) return 'd60';
  return 'd60mas';
}

const npos = (n) => (Number(n) > 0 ? Number(n) : null);

// Mismo puente de conversión que Cuentas por Pagar: todo pasa por el
// bolívar con las tasas que quedaron fijas en la factura.
export function aVista(v, vista, moneda, bcv, mercado) {
  const x = Number(v) || 0;
  if (vista === moneda) return x;
  const b = npos(bcv);
  const m = npos(mercado);
  let bs;
  if (moneda === 'BS') bs = x;
  else if (moneda === 'USD') { if (!m) return x; bs = x * m; }
  else { if (!b) return x; bs = x * b; }
  if (vista === 'USD') return m ? bs / m : x;
  if (vista === 'BCV') return b ? bs / b : x;
  return x;
}

const pref = (ver) => (ver === 'BCV' ? 'BCV$ ' : '$');
const prefMoneda = (m) => (m === 'BS' ? 'Bs ' : m === 'BCV' ? 'BCV$ ' : '$');
const vacio = () => ({ por_vencer: 0, d30: 0, d60: 0, d60mas: 0, total: 0 });

function diasTexto(d) {
  if (d < 0) return `Faltan ${-d}`;
  if (d === 0) return 'Vence hoy';
  return `${d}`;
}

// Excel o PDF del backend ({base}/antiguedad/excel|pdf) para el botón
// Exportar de la cabecera; lanza el error para que Exportar lo muestre.
export async function descargarAntiguedad(base, formato, verEn) {
  const resp = await api.get(`${base}/antiguedad/${formato}`, { params: { ver: verEn }, responseType: 'blob' });
  const m = /filename="?([^";]+)"?/i.exec(resp.headers?.['content-disposition'] || '');
  descargarArchivo(resp.data, m ? m[1] : `antiguedad.${formato === 'excel' ? 'xlsx' : 'pdf'}`);
}

export default function AntiguedadSaldos({ base, etiquetaParte, empresaActiva, verEn }) {
  const [facturas, setFacturas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [tramoFiltro, setTramoFiltro] = useState(null);
  const [abiertos, setAbiertos] = useState(() => new Set());

  useEffect(() => {
    if (!empresaActiva) return;
    setCargando(true);
    setError('');
    api.get(`${base}/antiguedad`)
      .then(({ data }) => setFacturas(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar la antigüedad de saldos'))
      .finally(() => setCargando(false));
  }, [base, empresaActiva]);

  const { filas, totales } = useMemo(() => {
    const grupos = new Map();
    const tot = vacio();
    facturas.forEach((f) => {
      const monto = aVista(f.saldo, verEn, f.moneda, f.tasa_bcv, f.tasa_mercado);
      const tramo = tramoDe(f.dias_vencido);
      if (!grupos.has(f.parte_id)) grupos.set(f.parte_id, { id: f.parte_id, nombre: f.parte_nombre, facturas: [], ...vacio() });
      const g = grupos.get(f.parte_id);
      g.facturas.push({ ...f, monto, tramo });
      g[tramo] += monto;
      g.total += monto;
      tot[tramo] += monto;
      tot.total += monto;
    });
    return { filas: [...grupos.values()].sort((a, b) => b.total - a.total), totales: tot };
  }, [facturas, verEn]);

  const visibles = tramoFiltro ? filas.filter((g) => g[tramoFiltro] > 0) : filas;

  function alternar(id) {
    setAbiertos((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }

  if (cargando) return <p className="text-slate-400 text-sm">Cargando…</p>;
  if (error) return <p className="text-red-600 text-sm">{error}</p>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {TRAMOS.map((t) => {
          const activo = tramoFiltro === t.key;
          const pct = totales.total > 0 ? Math.round((totales[t.key] / totales.total) * 100) : 0;
          return (
            <button
              key={t.key}
              onClick={() => setTramoFiltro(activo ? null : t.key)}
              title={activo ? 'Quitar filtro' : `Ver solo quienes tienen saldo ${t.key === 'por_vencer' ? 'por vencer' : `vencido ${t.label}`}`}
              className={`bg-white rounded-xl shadow-sm p-4 text-left transition-colors ${activo ? 'ring-2 ring-primary-600' : 'hover:bg-slate-50'}`}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t.label}</p>
              <p className={`text-xl font-bold mt-1 whitespace-nowrap ${t.clase}`}>{pref(verEn)}{formatMonto(totales[t.key])}</p>
              <p className="text-xs text-slate-400 mt-0.5">{pct}% del total</p>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b">
          <h2 className="font-semibold text-slate-700">
            Por {etiquetaParte.toLowerCase()}
            {tramoFiltro && <span className="font-normal text-slate-400"> · {TRAMOS.find((t) => t.key === tramoFiltro).label}</span>}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-3">{etiquetaParte}</th>
                {TRAMOS.map((t) => <th key={t.key} className="p-3 text-right whitespace-nowrap">{t.label}</th>)}
                <th className="p-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((g) => {
                const abierto = abiertos.has(g.id);
                return (
                  <Fragment key={g.id}>
                    <tr {...filaClickeable(() => alternar(g.id))} className={`border-t hover:bg-slate-50 transition-colors cursor-pointer ${FOCO_FILA}`}>
                      <td className="p-3 font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {abierto ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                          {g.nombre}
                          <span className="text-xs font-normal text-slate-400">({g.facturas.length})</span>
                        </span>
                      </td>
                      {TRAMOS.map((t) => (
                        <td key={t.key} className={`p-3 text-right whitespace-nowrap ${g[t.key] > 0 ? t.clase : 'text-slate-300'}`}>
                          {g[t.key] > 0 ? `${pref(verEn)}${formatMonto(g[t.key])}` : '—'}
                        </td>
                      ))}
                      <td className="p-3 text-right font-medium whitespace-nowrap">{pref(verEn)}{formatMonto(g.total)}</td>
                    </tr>
                    {abierto && (
                      <tr className="bg-slate-50">
                        <td colSpan={6} className="px-3 pb-3 pt-0">
                          <table className="w-full text-xs">
                            <thead className="text-slate-400 text-left">
                              <tr>
                                <th className="p-2 pl-6">N° factura</th>
                                <th className="p-2">Fecha</th>
                                <th className="p-2">Vence</th>
                                <th className="p-2 text-right">Días vencido</th>
                                <th className="p-2 text-right">Saldo original</th>
                                <th className="p-2 text-right">Saldo en {verEn}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {g.facturas.map((f) => {
                                const t = TRAMOS.find((x) => x.key === f.tramo);
                                return (
                                  <tr key={f.id} className="border-t border-slate-200">
                                    <td className="p-2 pl-6">#{f.numero}</td>
                                    <td className="p-2 whitespace-nowrap">{formatFecha(f.fecha)}</td>
                                    <td className="p-2 whitespace-nowrap">{formatFecha(f.fecha_vencimiento)}</td>
                                    <td className={`p-2 text-right whitespace-nowrap ${t.clase}`}>{diasTexto(Number(f.dias_vencido))}</td>
                                    <td className="p-2 text-right whitespace-nowrap">{prefMoneda(f.moneda)}{formatMonto(f.saldo)}</td>
                                    <td className="p-2 text-right whitespace-nowrap">{pref(verEn)}{formatMonto(f.monto)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {visibles.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-slate-400">
                    {filas.length === 0 ? 'No hay facturas pendientes.' : 'Nadie tiene saldo en este tramo.'}
                  </td>
                </tr>
              )}
            </tbody>
            {filas.length > 0 && (
              <tfoot>
                <tr className="border-t bg-slate-50 font-semibold">
                  <td className="p-3">Total</td>
                  {TRAMOS.map((t) => (
                    <td key={t.key} className="p-3 text-right whitespace-nowrap">{pref(verEn)}{formatMonto(totales[t.key])}</td>
                  ))}
                  <td className="p-3 text-right whitespace-nowrap">{pref(verEn)}{formatMonto(totales.total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
